import { Icon, Intent, NonIdealState, Position } from "@blueprintjs/core";
import classNames from "classnames";
import { EChartsRenderer, PieChart } from "components/charts";
import { FormLocationSelector } from "components/form-fields";
import { Numeric } from "components/formatting";
import { GridLayout } from "components/grid";
import { HubTile } from "components/hubs/hub-tile";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Tooltip } from "components/tooltip";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { EmployeeReviewsService } from "services";
import { SetFiltering } from "state/actions/Actions";
import { buildCompletedByMonthSeries } from "./overviewHelpers";

const completionSectionKey = "COMPLETION";
const outcomeLevelSectionKey = "OUTCOMELEVEL";
const completedSegmentKey = "COMPLETED";
const outcomeLevelFilterKey = "OutcomeLevel";
const signOffStatusFilterKey = "SignOffStatus";
const statusFilterKey = "Status";
const pendingReviewsPath = "/employee-reviews/pending-reviews";
const completedReviewsPath = "/employee-reviews/completed-reviews";
// Must match the filterName constant on the completed listing page.
const completedReviewsFilterName = "employeeReviewsCompleted";
const monthBarColour = "#008CFF";
const noCompletedReviewsMessage = "There are no completed reviews for the selected locations.";
// The number of skeleton tiles shown while the options are still loading. It has to be
// kept equal to the number of COMPLETION segments the options endpoint returns.
const loadingTileCount = 4;
const loadingTileIndexes = Array.from(Array(loadingTileCount).keys());

const emptyOptions = { statsSegments: [] };
const emptyStats = { stats: [], completedByMonth: [] };

// Drill-down: a segment's own filter decides where it goes. Outcome-level and
// sign-off-status segments filter the completed tab; the rest filter the pending tab
// by status.
function buildSegmentLink(segment) {
    if (segment.filterKey === outcomeLevelFilterKey) {
        return `${completedReviewsPath}?outcomeLevel=${segment.filterValues[0]}`;
    }

    if (segment.filterKey === signOffStatusFilterKey) {
        return `${completedReviewsPath}?signOffStatus=${segment.filterValues[0]}`;
    }

    // The options response is cached server-side for a day, so the Completed segment can
    // still arrive without its filter after a release. Its count excludes reviews awaiting
    // sign-off either way, so neither destination matches the count in that window; the
    // unfiltered completed list is the less wrong of the two.
    if (segment.key === completedSegmentKey) {
        return completedReviewsPath;
    }

    if (segment.filterKey === statusFilterKey) {
        return `${pendingReviewsPath}?status=${segment.filterValues.join(",")}`;
    }

    // A segment whose filter this page has no route for still has to land somewhere
    // harmless. The pending listing persists whatever status it is handed, so passing an
    // unknown filter's values on as a status would 400 its every later search.
    return pendingReviewsPath;
}

// The completion segments that land on the completed listing clear its persisted funnel
// before navigating, so a stored selection cannot narrow the list below the count the
// tile showed.
function needsFunnelReset(segment) {
    return segment.key === completedSegmentKey || segment.filterKey === signOffStatusFilterKey;
}

export function EmployeeReviewsOverview() {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [isInitiallyLoading, setIsInitiallyLoading] = useState(true);
    const [options, setOptions] = useState(emptyOptions);
    const [stats, setStats] = useState(emptyStats);

    const reduxLocations = useSelector((state) => state.employeeReviews.locations);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);

    function showLoadError() {
        NotificationToaster.show(Intent.DANGER, "Unable to load data. Please refresh the page.");
    }

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }

        setIsLoading(true);

        if (isInitiallyLoading) {
            // The stats segments are static, so the options only load with the first
            // stats request; later location changes just refresh the counts.
            Promise.all([
                EmployeeReviewsService.getOptions(),
                EmployeeReviewsService.getStats(reduxLocations)
            ]).then(
                ([optionsResult, statsResult]) => {
                    setOptions(optionsResult);
                    setStats(statsResult);
                },
                showLoadError
            ).finally(() => {
                setIsInitiallyLoading(false);
                setIsLoading(false);
            });
        } else {
            EmployeeReviewsService.getStats(reduxLocations).then(
                (statsResult) => {
                    setStats(statsResult);
                },
                showLoadError
            ).finally(() => {
                setIsLoading(false);
            });
        }
    }, [reduxLocations]);

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_REVIEWS_LOCATIONS",
            payload: locations
        });
    }

    function segmentsForSection(sectionKey) {
        return options.statsSegments
            .filter((segment) => segment.sectionKey === sectionKey)
            .sort((a, b) => a.order - b.order);
    }

    function countForSegment(segment) {
        const matchingStat = (stats.stats ?? []).find((stat) => stat.id === segment.id);
        return matchingStat?.count ?? 0;
    }

    function buildChartItems(segments) {
        return segments.map((segment) => ({
            label: segment.name,
            value: countForSegment(segment),
            color: `#${segment.colour}`,
            segment: segment
        }));
    }

    const completionSegments = segmentsForSection(completionSectionKey);
    let completionItems = buildChartItems(completionSegments);
    let outcomeItems = buildChartItems(segmentsForSection(outcomeLevelSectionKey));

    const totalReviews = completionItems.reduce((sum, item) => sum + item.value, 0);
    const totalWithOutcome = outcomeItems.reduce((sum, item) => sum + item.value, 0);

    // All-zero counts would render as equal coloured segments, so hand the pie
    // an empty series and let its no-data state show instead.
    if (totalReviews === 0) {
        completionItems = [];
    }

    if (totalWithOutcome === 0) {
        outcomeItems = [];
    }

    const completedSegment = completionSegments.find((segment) => segment.key === completedSegmentKey);

    let completedCount = 0;
    if (completedSegment != null) {
        completedCount = countForSegment(completedSegment);
    }

    let percentComplete = 0;
    if (totalReviews > 0) {
        percentComplete = Math.round((completedCount / totalReviews) * 100);
    }

    function onCompletedDrillDown() {
        // Clear the listing's persisted funnel before handing over, so a stored
        // selection cannot combine with the drill-down and under-report against the
        // tile's count.
        dispatch(SetFiltering(completedReviewsFilterName, []));
    }

    let completionTiles;
    if (completionSegments.length > 0) {
        completionTiles = completionSegments.map((segment) => {
            const tile = (
                <HubTile
                    isLoading={isLoading}
                    title={segment.name}
                    count={countForSegment(segment)}
                    colour={`#${segment.colour}`}
                    link={buildSegmentLink(segment)}
                    fullWidth
                />
            );

            if (needsFunnelReset(segment)) {
                // Capture phase so the filter reset dispatches before the tile's
                // Link navigates away from this page.
                return (
                    <div key={segment.id} onClickCapture={onCompletedDrillDown}>
                        {tile}
                    </div>
                );
            }

            return (
                <div key={segment.id}>
                    {tile}
                </div>
            );
        });
    } else {
        completionTiles = loadingTileIndexes.map((index) => (
            <HubTile key={`completion-placeholder-${index}`} isLoading fullWidth />
        ));
    }

    function onSliceClick(sliceItem) {
        if (needsFunnelReset(sliceItem.segment)) {
            onCompletedDrillDown();
        }
        navigate(buildSegmentLink(sliceItem.segment));
    }

    // Fixed 12-month window ending this month — the API omits empty months, so the
    // helper zero-fills them to keep the axis complete.
    const monthlyData = buildCompletedByMonthSeries(stats.completedByMonth, moment());
    const hasMonthData = monthlyData.some((month) => month.count > 0);

    const monthChartOption = {
        tooltip: { show: true },
        grid: { left: 40, right: 20, top: 30, bottom: 30 },
        xAxis: {
            type: "category",
            data: monthlyData.map((month) => month.label)
        },
        // minInterval keeps the count axis to whole-number ticks.
        yAxis: { type: "value", minInterval: 1 },
        series: [
            {
                type: "bar",
                data: monthlyData.map((month) => month.count),
                itemStyle: { color: monthBarColour },
                label: { show: true, position: "top" },
                cursor: "pointer"
            }
        ]
    };

    function onMonthBarClick(params) {
        if (params.componentType !== "series") {
            return;
        }

        const month = monthlyData[params.dataIndex];
        if (month == null) {
            return;
        }

        // The month bar drills into the completed tab bounded to that calendar month (inclusive).
        const monthStart = moment({ year: month.year, month: month.month - 1, day: 1 });
        const completedAfter = monthStart.format("YYYY-MM-DD");
        const completedBefore = monthStart.clone().endOf("month").format("YYYY-MM-DD");

        navigate(`${completedReviewsPath}?completedAfter=${completedAfter}&completedBefore=${completedBefore}`);
    }

    return (
        <>
            <div className="inline-items spacer-bottom">
                <FormLocationSelector
                    businessArea={"Tacho"}
                    loading={isInitiallyLoading}
                    selectedLocations={reduxLocations}
                    setSelectedLocations={onSelectedLocationsChange}
                    useLocationDefaults={useLocationDefaults}
                    useHierarchy={useHierarchy}
                    setUseHierarchy={setUseHierarchy}
                    locationDefaultsToTrue={isLoading ? null : true}
                />
                <Tooltip
                    position={Position.RIGHT}
                    content={
                        <div>
                            <h4>Location Filter</h4>
                            <p>
                                This location filter is applied to all tabs in this hub.
                            </p>
                        </div>
                    }
                >
                    <Icon
                        className="hub-tool-tip"
                        icon="info-sign"
                        size={15}
                        color="#0C2235"
                    />
                </Tooltip>
            </div>

            <GridLayout numberOfColumns={4} gap={30} className="spacer-bottom">
                {completionTiles}
            </GridLayout>

            <GridLayout numberOfColumns={2} gap={30} className="spacer-bottom">
                <PieChart
                    title="Review completion"
                    loading={isLoading}
                    items={completionItems}
                    noDataMessage="There are no reviews for the selected locations."
                    centerValue={(
                        <>
                            <span>{percentComplete}%</span>
                            <br />
                            <span className="chart-title">Complete</span>
                        </>
                    )}
                    onSliceClick={onSliceClick}
                />
                <PieChart
                    title="Outcome levels"
                    loading={isLoading}
                    items={outcomeItems}
                    noDataMessage={noCompletedReviewsMessage}
                    centerValue={(
                        <>
                            <span><Numeric>{totalWithOutcome}</Numeric></span>
                            <br />
                            <span className="chart-title">Completed</span>
                        </>
                    )}
                    onSliceClick={onSliceClick}
                />
            </GridLayout>

            <div className="chart-container spacer-bottom">
                <span className="chart-title">CHART</span>
                <h3 className="spacer-bottom">Reviews completed by month</h3>
                <ShowHide
                    evaluator={hasMonthData || isLoading}
                    show={(
                        <div className={classNames({ "bp6-skeleton": isLoading })}>
                            <EChartsRenderer
                                option={monthChartOption}
                                onEvents={{ click: onMonthBarClick }}
                            />
                        </div>
                    )}
                    hide={(
                        <NonIdealState
                            title={''}
                            className='spacer-bottom-small'
                            description={noCompletedReviewsMessage}
                        />
                    )}
                />
            </div>
        </>
    );
}
